import React, { useEffect, useState } from "react";
import { FiCheckSquare, FiClipboard } from "react-icons/fi";

export type ParentActionsView = "open" | "school-tasks";

interface Props {
  activeView: ParentActionsView;
  onChange: (view: ParentActionsView) => void;
  openActionsCount: number;
  schoolTasksCount: number | null;
  showSchoolTasks: boolean;
  openActionsLabel: string;
  schoolTasksLabel: string;
  className?: string;
}

export default function ParentActionsSubnav({
  activeView,
  onChange,
  openActionsCount,
  schoolTasksCount,
  showSchoolTasks,
  openActionsLabel,
  schoolTasksLabel,
  className = "",
}: Props) {
  const [touchExpanded, setTouchExpanded] = useState(false);
  useEffect(() => setTouchExpanded(false), [activeView]);

  if (!showSchoolTasks) return null;

  const items = {
    open: { key: "open" as const, label: openActionsLabel, Icon: FiCheckSquare, count: openActionsCount },
    "school-tasks": { key: "school-tasks" as const, label: schoolTasksLabel, Icon: FiClipboard, count: schoolTasksCount },
  };
  const active = items[activeView];
  const alternative = items[activeView === "open" ? "school-tasks" : "open"];
  const isTouchInteraction = () =>
    typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  const renderCard = (
    item: typeof active,
    position: "active" | "alternative",
  ) => {
    const { key, label, Icon, count } = item;
    return (
      <button
        key={key}
        type="button"
        aria-current={position === "active" ? "page" : undefined}
        onClick={() => {
          if (position === "active") {
            if (isTouchInteraction()) setTouchExpanded((expanded) => !expanded);
            return;
          }
          if (isTouchInteraction() && !touchExpanded) {
            setTouchExpanded(true);
            return;
          }
          setTouchExpanded(false);
          onChange(key);
        }}
        className={`parent-actions-stack-card parent-actions-stack-card--${position}`}
      >
        <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        {count !== null && count > 0 && (
          <span className="min-w-5 shrink-0 rounded-full bg-white/90 px-1.5 py-0.5 text-center text-[0.7rem] font-bold text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100">
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className={`parent-actions-stack relative -ml-3 flex h-14 w-[9.75rem] min-w-0 items-center pl-3 ${touchExpanded ? "is-expanded" : ""} ${className}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setTouchExpanded(false);
      }}
    >
      <span className="parent-actions-stack-rail" aria-hidden="true" />
      <div className="relative h-14 min-w-0 flex-1">
        {renderCard(active, "active")}
        {renderCard(alternative, "alternative")}
      </div>
      <style>{`
        @keyframes parentActionsStackCardIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; }
        }
        .parent-actions-stack { z-index: 20; }
        .parent-actions-stack-rail {
          background: rgba(52, 211, 153, 0.82);
          border-radius: 999px;
          height: 3px;
          left: 0;
          position: absolute;
          right: 0;
          top: 1.72rem;
        }
        .parent-actions-stack-card {
          align-items: center;
          background: rgba(236, 253, 245, 0.97);
          border: 1px solid rgb(167 243 208);
          color: rgb(6 95 70);
          display: flex;
          font-size: 0.75rem;
          font-weight: 650;
          gap: 0.375rem;
          height: 2.75rem;
          left: 0;
          padding: 0.5rem 0.65rem;
          position: absolute;
          right: 0;
          top: 0.35rem;
          transition: transform 225ms cubic-bezier(0.22, 1, 0.36, 1), width 225ms cubic-bezier(0.22, 1, 0.36, 1), left 225ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease;
        }
        .parent-actions-stack-card--active {
          animation: parentActionsStackCardIn 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
          border-radius: 0.3rem 0.85rem 0.85rem 0.3rem;
          box-shadow: 0 4px 10px rgba(15, 118, 110, 0.08);
          z-index: 2;
        }
        .parent-actions-stack-card--alternative {
          border-radius: 0.25rem 0.7rem 0.7rem 0.25rem;
          left: 0.4rem;
          right: 0.4rem;
          transform: translateY(0.75rem);
          z-index: 1;
        }
        .parent-actions-stack:hover .parent-actions-stack-card--alternative,
        .parent-actions-stack:focus-within .parent-actions-stack-card--alternative,
        .parent-actions-stack.is-expanded .parent-actions-stack-card--alternative {
          box-shadow: 0 8px 18px rgba(15, 118, 110, 0.12);
          transform: translateY(3.15rem);
        }
        .parent-actions-stack-card:hover { border-color: rgb(110 231 183); }
        .parent-actions-stack-card:focus-visible {
          outline: 2px solid rgb(16 185 129);
          outline-offset: 2px;
        }
        .dark .parent-actions-stack-rail { background: rgb(4 120 87); }
        .dark .parent-actions-stack-card {
          background: rgba(2, 44, 34, 0.97);
          border-color: rgb(6 78 59);
          color: rgb(167 243 208);
        }
        @media (prefers-reduced-motion: reduce) {
          .parent-actions-stack-card { animation: none; transition: none; }
        }
      `}</style>
    </div>
  );
}
